import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type CatalogBrandItemDTO, type TenantBrandViewDTO, TenantConfigPlane, ValidationErrorCode } from '@today/types'
import { isAxiosError } from 'axios'
import { useEffect, useState } from 'react'
import { tenantBrandService } from '@/services/tenant-brand.service'

interface ApiErrorBody {
	error?: { code?: string }
}

const viewKey = (plane: TenantConfigPlane, ref: string) => ['tenant-brand', plane, ref] as const

/** The message a refused save shows. */
export const brandSaveErrorOf = (error: unknown): string => {
	const code = isAxiosError<ApiErrorBody>(error) ? error.response?.data?.error?.code : undefined
	return code === ValidationErrorCode.BRAND_NOT_SELECTABLE
		? 'This brand cannot be selected — only published brands can.'
		: 'The brand could not be saved — try again.'
}

/**
 * A tenant's brand in the console (TO-380). The view is read from the plane's
 * route; only an editable section (platform plane) loads the catalogue and
 * offers a save. A refused save restores the previous selection.
 */
export function useTenantBrand(args: {
	plane: TenantConfigPlane
	tenantId: string
	tenantSlug: string
	editable: boolean
}) {
	const { plane, tenantId, tenantSlug, editable } = args
	const isPlatform = plane === TenantConfigPlane.PLATFORM
	const client = useQueryClient()
	const key = viewKey(plane, isPlatform ? tenantId : tenantSlug)

	const view = useQuery<TenantBrandViewDTO>({
		queryKey: key,
		queryFn: () => (isPlatform ? tenantBrandService.getPlatform(tenantId) : tenantBrandService.getTenant(tenantSlug)),
	})
	const catalog = useQuery<CatalogBrandItemDTO[]>({
		queryKey: ['brand-catalog'],
		queryFn: () => tenantBrandService.listCatalog(),
		enabled: editable && isPlatform,
	})

	const storedId = view.data?.brandId ?? ''
	const [selectedId, setSelectedId] = useState('')
	useEffect(() => setSelectedId(storedId), [storedId])

	const save = useMutation({
		mutationFn: (brandId: string) => tenantBrandService.select(tenantId, brandId),
		onSuccess: (saved) => client.setQueryData(key, saved),
		onError: () => setSelectedId(storedId),
	})

	return {
		view: view.data ?? null,
		loadFailed: view.isError,
		reload: () => void view.refetch(),
		catalog: catalog.data ?? null,
		selectedId,
		select: (id: string) => {
			save.reset()
			setSelectedId(id)
		},
		save: () => save.mutate(selectedId),
		saving: save.isPending,
		saveError: save.isError ? brandSaveErrorOf(save.error) : null,
	}
}
